import React, {useEffect, useState} from 'react';
import {Box, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {useSalesmanContext} from '../../../../../../context/salesman';
import useToast from '../../../../../../hooks';
import InternalTemplate from '../../../../../../components/templates/internal';
import {StackParamList} from '../../../../../../navigators';
import {SpinnerModal} from '../../../../../../components/modals';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../components/buttons';
import {TextInput} from '../../../../../../components/inputs/text';
import SimpleRadio from '../../../../../../components/inputs/simple-radio';
import {
  IOrder,
  IOrderTransport,
} from '../../../../../../context/commons/interfaces/index';
import {IdentityInput} from '../../../../../../components/inputs/masked';
import {
  RESULT_REQUEST_CACHED_FAILED,
  RESULT_REQUEST_CACHED_OK,
} from '../../../../../../interfaces/types/result-of-request-cached';

type ScreenProps = NativeStackScreenProps<StackParamList, 'SalesmanOrderEnd'>;

enum ShowTrailerEnum {
  yes = 'yes',
  false = 'no',
}

interface ITransportForm {
  driver?: string;
  identity?: string;
  plate?: string;
  vehicle?: string;
  hasTrailer?: string;
  trailerPlate?: string;
  provider?: string;
}

const SalesmanOrderEndScreen = ({navigation, route}: ScreenProps) => {
  const {customerOrder, sendSalesmanNewOrder: sendOrder} = useSalesmanContext();
  const {
    watch,
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {
      driver: undefined,
      identity: '',
      plate: undefined,
      vehicle: undefined,
      hasshot: false,
      shotplate: undefined,
      provider: undefined,
    } as ITransportForm,
  });
  const [isLoading, setIsLoading] = useState(false);
  const {showInputsError, showServerError, showOrderError} = useToast();
  const showHasTrailer = watch('hasTrailer');

  useEffect(() => {
    setIsLoading(false);
  }, [route]);

  const showHasTrailerInput = () => {
    if (showHasTrailer) {
      return showHasTrailer === ShowTrailerEnum.yes;
    }
    return false;
  };

  const handleGoBack = () => {
    navigation.pop(1);
  };

  const handleOnContinueSubmit: SubmitHandler<ITransportForm> = async (
    data: ITransportForm,
  ) => {
    try {
      setIsLoading(true);
      if (customerOrder) {
        const {driver, identity, vehicle, plate, trailerPlate, provider} = data;
        customerOrder.transport = {
          driver: driver ?? '',
          vehicle: vehicle ?? '',
          identity: identity ?? '',
          plate: plate ?? '',
          hasTrailer: showHasTrailerInput(),
          trailerPlate: trailerPlate ?? '',
          provider: provider ?? '',
        } as IOrderTransport;
        const order = {
          ...customerOrder,
          transport: customerOrder.transport,
        } as IOrder;

        const result = await sendOrder(order);

        setIsLoading(false);
        if (result === RESULT_REQUEST_CACHED_OK) {
          navigation.navigate('SalesmanOrderSuccess');
        } else if (result === RESULT_REQUEST_CACHED_FAILED) {
          showOrderError();
        } else {
          const {request} = result;
          navigation.navigate('WithoutConnectionConfirm', {
            request,
            workflow: 'salesman-order',
          });
        }
      }
    } catch (e) {
      setIsLoading(false);
      showServerError();
    }
  };

  const onInvalid: SubmitErrorHandler<ITransportForm> = async (
    fieldErrors: FieldErrors<ITransportForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleClearForm = () => {
    reset();
  };

  return (
    <InternalTemplate
      title={'salesman-order-end-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />
      <VStack paddingX={5} paddingBottom={150}>
        <VStack>
          <TextInput
            control={control}
            name="provider"
            label={'salesman-order-trasnport-provider'}
            error={errors.provider}
            rules={{
              required: 'is-required',
            }}
          />
          <TextInput
            control={control}
            name="driver"
            label={'salesman-order-trasnport-driver'}
            error={errors.driver}
            rules={{
              required: 'is-required',
            }}
          />
          <IdentityInput
            control={control}
            name="identity"
            label={'salesman-order-trasnport-identiy'}
            error={errors.identity}
            rules={{
              required: 'form-is-required',
            }}
          />
          <TextInput
            control={control}
            name="plate"
            label={'salesman-order-trasnport-plate'}
            error={errors.plate}
            rules={{
              required: 'is-required',
            }}
          />
          <TextInput
            control={control}
            name="vehicle"
            label={'salesman-order-trasnport-vehicle'}
            error={errors.vehicle}
            rules={{
              required: 'is-required',
            }}
          />
          <SimpleRadio
            control={control}
            name="hasTrailer"
            label={'salesman-order-trasnport-hastrailer'}
            options={[
              {label: 'Yes', value: ShowTrailerEnum.yes},
              {label: 'No', value: ShowTrailerEnum.false},
            ]}
            error={errors.hasTrailer}
            defaultValue={ShowTrailerEnum.false}
            isRequired={true}
          />
          {showHasTrailerInput() && (
            <TextInput
              control={control}
              name="trailerPlate"
              label={'salesman-order-trasnport-trailerplate'}
              error={errors.trailerPlate}
              rules={{
                required: 'is-required',
              }}
            />
          )}

          <Box my={5}>
            <PrimaryButton
              label="salesman-order-end-continue-button"
              onPress={handleSubmit(handleOnContinueSubmit, onInvalid)}
            />
          </Box>
          <Box my={2}>
            <SecondaryButton
              label="salesman-ordertransport-form-button-cancel"
              onPress={handleClearForm}
            />
          </Box>
        </VStack>
        <Box height={100}>
          <></>
        </Box>
      </VStack>
    </InternalTemplate>
  );
};
export default SalesmanOrderEndScreen;
